import React, { useState } from "react"
import {
  Card,
  CardContent,
  CardActionArea,
  Typography,
  IconButton,
  makeStyles,
  TextField,
  Box,
  Tooltip,
  Menu,
  MenuItem,
} from "@material-ui/core"
// @ts-ignore
import { useHistory } from "react-router-dom"
import moment from "moment"
import { TrashIcon2 } from "src/components/icons-svg"

import EditIcon from "@material-ui/icons/Edit"
import CheckIcon from "@material-ui/icons/Check"
import CloseIcon from "@material-ui/icons/Close"
import MoreVertIcon from "@material-ui/icons/MoreVert"
import DeleteIcon from "@material-ui/icons/Delete"
import { useAlertStore } from "src/v2/utils/alert-store"
import useStore from "src/v2/utils/store"
import CustomCheckbox from "../form-components/custom-checkbox"
import CustomButton from "../form-components/custom-button"
import consts from "src/v2/utils/consts.json"

const useStyles = makeStyles((theme) => ({
  card: {
    position: "relative",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    borderRadius: 16,
    background: "#fff",
    boxShadow: "0 8px 20px rgba(0,0,0,0.15), 0 2px 8px rgba(0,0,0,0.1)",
    transition: "box-shadow 0.3s, transform 0.2s",
    "&:hover": {
      boxShadow: "0 12px 28px rgba(0,0,0,0.18), 0 5px 10px rgba(0,0,0,0.12)",
      transform: "translateY(-4px) scale(1.01)",
      "& $menuButton": {
        opacity: 1,
        pointerEvents: "auto",
      },
      "& $reviewCheckbox": {
        opacity: 1,
        pointerEvents: "auto",
      },
    },
  },
  cardActionAreaHighlight: {
    "& .MuiCardActionArea-focusHighlight": {
      background: "#fff",
    },
  },
  cardContent: {
    flexGrow: 1,
    paddingBottom: `${theme.spacing(2)}px !important`,
    "&:hover": {
      background: "#fff",
    },
  },
  reviewName: {
    color: theme.palette.primary.main,
    fontWeight: 600,
    cursor: "pointer",
    fontSize: 20,
    marginBottom: theme.spacing(1),
    width: "100%",
    paddingRight: theme.spacing(5), // Make space for the menu icon
  },
  reviewNameText: {
    display: "inline",
    wordBreak: "break-word",
    overflowWrap: "anywhere",
  },
  reviewId: {
    fontSize: 13,
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    wordBreak: "break-all",
  },
  created: {
    fontSize: 13,
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(1),
  },
  menuButton: {
    position: "absolute",
    top: theme.spacing(1),
    right: theme.spacing(1),
    zIndex: 10,
    color: theme.palette.text.secondary,
    opacity: 1,
    pointerEvents: "auto",
    transition: "color 0.2s",
    padding: 4, // Smaller padding for just the icon
    backgroundColor: "transparent", // Remove background
    boxShadow: "none", // Remove shadow
    "&:hover": {
      backgroundColor: "transparent", // Keep transparent on hover
      color: theme.palette.primary.main, // Change color on hover instead
    },
  },
  reviewCheckbox: {
    position: "absolute",
    top: theme.spacing(1),
    left: theme.spacing(1),
    zIndex: 10,
    transition: "opacity 0.2s",
    background: "#fff",
    borderRadius: "50%",
    opacity: 0,
    pointerEvents: "none",
    // Show on card hover
    "$card:hover &": {
      opacity: 1,
      pointerEvents: "auto",
    },
  },
  reviewCheckboxVisible: {
    opacity: 1,
    pointerEvents: "auto",
  },
  editInput: {
    marginBottom: theme.spacing(1),
    "& .MuiInputBase-root": {
      fontSize: 20,
      fontWeight: 600,
      color: theme.palette.primary.main,
    },
    width: "calc(100% - 48px)", // Subtract space for menu icon
    maxWidth: "85%", // Prevent extending too far
  },
  editContainer: {
    position: "relative",
    zIndex: 15, // Higher than other elements
  },
  menuItem: {
    minHeight: "auto",
    padding: theme.spacing(1, 2),
  },
  menuItemIcon: {
    marginRight: theme.spacing(1),
    fontSize: 20,
  },
  editActions: {
    display: "flex",
    alignItems: "center",
    zIndex: 50, // Ensure these are above other elements
  },
  actionIcon: {
    padding: 4,
  },
}))

const ReviewCard = ({ review, isSelected, onSelect, onDelete }) => {
  const classes = useStyles()
  const history = useHistory()
  const updateReview = useStore((state) => state.updateReview)
  const showAlert = useAlertStore((state) => state.showAlert)
  
  const [isEditing, setIsEditing] = useState(false)
  const [editedName, setEditedName] = useState(review.name || "")
  const [nameError, setNameError] = useState("")
  
  // Menu state
  const [menuAnchorEl, setMenuAnchorEl] = useState(null)
  const isMenuOpen = Boolean(menuAnchorEl)
  
  const handleCardClick = () => {
    if (!isEditing) {
      history.push(`${consts.ROUTES.REPORTS}/${review._id}`)
    }
  }
  
  // Menu handlers
  const handleMenuOpen = (event) => {
    event.stopPropagation()
    setMenuAnchorEl(event.currentTarget)
  }
  
  const handleMenuClose = (event) => {
    if (event) event.stopPropagation()
    setMenuAnchorEl(null)
  }
  
  const handleEditClick = (event) => {
    if (event) event.stopPropagation()
    setMenuAnchorEl(null)
    setIsEditing(true)
    setEditedName(review.name || "")
    setNameError("")
  }
  
  const handleDeleteClick = (event) => {
    if (event) event.stopPropagation()
    setMenuAnchorEl(null)
    // Directly call the parent's onDelete without showing confirmation
    onDelete(review._id)
  }
  
  const handleCancelEdit = (e) => {
    if (e) e.stopPropagation()
    setIsEditing(false)
    setEditedName(review.name || "")
    setNameError("")
  }
  
  const validateName = (name) => {
    const trimmedName = name.trim()
    if (!trimmedName) {
      setNameError("Review name is required")
      return false
    }
    setNameError("")
    return true
  }
  
  const handleSaveEdit = async (e) => {
    if (e) e.stopPropagation()
    
    const trimmedName = editedName.trim()
    if (!validateName(trimmedName)) {
      return
    }
    
    try {
      // Only update if the name has changed
      if (trimmedName !== review.name) {
        await updateReview("name", trimmedName, review._id, showAlert)
      }
      
      setIsEditing(false)
    } catch (error) {
      // If there was an error, keep the editing mode open
      console.error("Error updating review name:", error)
    }
  }
  
  const handleNameChange = (e) => {
    setEditedName(e.target.value)
    // Only validate if there was already an error (for real-time validation)
    if (nameError) {
      validateName(e.target.value)
    }
  }
  
  const handleNameKeyDown = (e) => {
    if (e.key === "Enter") {
      handleSaveEdit(e)
    } else if (e.key === "Escape") {
      handleCancelEdit(e)
    }
  }

  // Render different content based on editing state
  const renderCardContent = () => {
    return (
      <CardContent className={classes.cardContent}>
        {isEditing ? (
          <Box className={classes.editContainer}>
            <TextField
              value={editedName}
              onChange={handleNameChange}
              onKeyDown={handleNameKeyDown}
              autoFocus
              error={!!nameError}
              helperText={nameError}
              className={classes.editInput}
              variant="standard"
              InputProps={{
                endAdornment: (
                  <Box className={classes.editActions}>
                    <IconButton 
                      size="small" 
                      className={classes.actionIcon}
                      onClick={handleSaveEdit}
                    >
                      <CheckIcon color="primary" />
                    </IconButton>
                    <IconButton 
                      size="small" 
                      className={classes.actionIcon}
                      onClick={handleCancelEdit}
                    >
                      <CloseIcon color="error" />
                    </IconButton>
                  </Box>
                ),
              }}
            />
          </Box>
        ) : (
          <Typography variant="h6" className={classes.reviewName}>
            <span className={classes.reviewNameText}>{review.name}</span>
          </Typography>
        )}
        <Typography className={classes.reviewId}>
          Review ID: {String(review._id || 'N/A')}
        </Typography>
        <Typography className={classes.created}>
          Created:{" "}
          {review.created_on
            ? moment(review.created_on).format("DD-MMM-YYYY, hh:mm a")
            : "N/A"}
        </Typography>
      </CardContent>
    );
  };

  return (
    <Card className={classes.card}>
      <span
        className={
          isSelected
            ? `${classes.reviewCheckbox} ${classes.reviewCheckboxVisible}`
            : classes.reviewCheckbox
        }
      >
        <CustomCheckbox
          checked={isSelected}
          label=""
          onChange={onSelect}
          style={{ padding: 0, margin: 0 }}
        />
      </span>
      
      {/* Menu Button */}
      <IconButton
        className={classes.menuButton}
        aria-label="more options"
        onClick={handleMenuOpen}
        size="small"
      >
        <MoreVertIcon />
      </IconButton>
      
      {/* Dropdown Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={isMenuOpen}
        onClose={handleMenuClose}
        onClick={(e) => e.stopPropagation()} // Prevent card click when interacting with menu
        getContentAnchorEl={null}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={handleEditClick} className={classes.menuItem}>
          <EditIcon className={classes.menuItemIcon} fontSize="small" />
          Edit
        </MenuItem>
        <MenuItem onClick={handleDeleteClick} className={classes.menuItem}>
          <DeleteIcon className={classes.menuItemIcon} fontSize="small" color="error" />
          Delete
        </MenuItem>
      </Menu>
      
      {isEditing ? (
        // When editing, don't use CardActionArea
        renderCardContent()
      ) : (
        // When not editing, wrap in CardActionArea
        <CardActionArea
          className={classes.cardActionAreaHighlight}
          onClick={handleCardClick}
        >
          {renderCardContent()}
        </CardActionArea>
      )}
    </Card>
  );
}

export default ReviewCard